import { useRef, lazy, Suspense, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, Phone, Building2 } from "lucide-react";
import { CONTACT } from "@/lib/content";
import { MEDIA } from "@/lib/media";
import { useLang } from "@/lib/i18n";
import Bubbles from "@/components/Bubbles";

// Code-split the heavy Three.js bundle so it never blocks first paint.
const WaterBlob = lazy(() => import("@/components/WaterBlob"));

// Detect CPU/software WebGL (SwiftShader, llvmpipe, Microsoft Basic Render, or no WebGL).
// On those clients the 3D loop is prohibitively expensive on the main thread, so we skip
// the canvas entirely and let the existing animated CSS gradient-orb layer stand in.
function isSoftwareRenderer() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return true;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const r = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : "";
    return /swiftshader|llvmpipe|software|microsoft basic render/i.test(r);
  } catch (e) {
    return true;
  }
}

// Only render the 3D canvas on capable, motion-friendly desktop viewports with a real GPU.
function use3DEnabled() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (isSoftwareRenderer()) return;
    const desktop = window.matchMedia("(min-width: 768px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setEnabled(desktop.matches && !reduce.matches);
    update();
    desktop.addEventListener("change", update);
    reduce.addEventListener("change", update);
    return () => {
      desktop.removeEventListener("change", update);
      reduce.removeEventListener("change", update);
    };
  }, []);
  return enabled;
}

const MaskLine = ({ children, delay }) => (
  <span className="line-mask">
    <span className="block anim-line" style={{ animationDelay: `${delay}s` }}>
      {children}
    </span>
  </span>
);

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
};

// Animated background orbs. On lite-mode clients (software GPU / reduced-motion) these render
// static so framer-motion's continuous rAF loop never spins (it otherwise re-composites the full
// viewport every frame — catastrophic under software rendering). Positions match the animation
// start values, so the look is identical at rest.
const ORBS = [
  {
    className: "absolute right-[12%] top-[18%] h-[42vh] w-[42vh] rounded-full bg-aqua/25 blur-[90px]",
    animate: { x: [0, 60, -20, 0], y: [0, -40, 30, 0], scale: [1, 1.15, 0.95, 1] },
    transition: { duration: 18, repeat: Infinity, ease: "easeInOut" },
  },
  {
    className: "absolute left-[8%] top-[30%] h-[36vh] w-[36vh] rounded-full bg-emerald-500/20 blur-[100px]",
    animate: { x: [0, -50, 30, 0], y: [0, 40, -30, 0], scale: [1, 1.2, 1, 1] },
    transition: { duration: 22, repeat: Infinity, ease: "easeInOut" },
  },
  {
    className: "absolute right-[30%] bottom-[10%] h-[30vh] w-[30vh] rounded-full bg-[#6EC6E8]/15 blur-[110px]",
    animate: { x: [0, 30, -40, 0], y: [0, -30, 20, 0] },
    transition: { duration: 26, repeat: Infinity, ease: "easeInOut" },
  },
];

// Software WebGL / reduced-motion => "lite mode": no continuous JS animation loops.
function useLiteMode() {
  const [lite] = useState(() => {
    try {
      return isSoftwareRenderer() || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      return true;
    }
  });
  return lite;
}

export default function Hero() {
  const { t } = useLang();
  const enable3D = use3DEnabled();
  const lite = useLiteMode();
  const ref = useRef(null);
  const [inView, setInView] = useState(true);
  // Pause (unmount) the 3D canvas whenever the hero scrolls out of view so its render
  // loop does zero work while the visitor reads the rest of the page.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Aquatic Ottawa water backdrop — the whole hero is plunged into crystal-clear water */}
      <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden>
        <img
          src={MEDIA.ottawaHero.src}
          srcSet={MEDIA.ottawaHero.srcSet}
          sizes="100vw"
          alt="Bright Ottawa skyline along the Ottawa River with a fresh water splash"
          fetchPriority="high"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,26,46,0.35) 0%, rgba(5,26,46,0.15) 45%, rgba(5,26,46,0.85) 100%)" }} />
      </div>

      {/* radial glow + animated liquid orbs (always-visible depth) */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 h-[80vh] w-[80vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-aqua/10 blur-[120px]" />
        {ORBS.map((o, i) =>
          lite ? (
            <div key={i} className={o.className} />
          ) : (
            <motion.div key={i} animate={o.animate} transition={o.transition} className={o.className} />
          )
        )}
      </div>

      {/* 3D water blobs — desktop + real GPU only, lazy-loaded, disabled under reduced-motion,
          and unmounted while the hero is offscreen. */}
      {enable3D && inView && (
        <motion.div style={{ y: yBlob, opacity }} className="absolute inset-0 z-[1]" aria-hidden>
          <Suspense fallback={null}>
            <WaterBlob />
          </Suspense>
        </motion.div>
      )}

      {/* Legibility scrim: darkens the lower-left text column over the 3D blob */}
      <div
        className="pointer-events-none absolute inset-0 z-[5]"
        aria-hidden
        style={{ background: "linear-gradient(to top, rgba(4,17,32,0.9) 0%, rgba(4,17,32,0.5) 38%, rgba(4,17,32,0) 68%)" }}
      />

      <Bubbles />

      {/* Mobile-only: single 18 L Odessa bottle as a secondary product visual (far bottom-right, clears copy/CTAs) */}
      <div className="pointer-events-none absolute bottom-0 right-0 z-[6] w-[26vw] max-w-[104px] md:hidden" data-testid="hero-bottle-mobile">
        <img src={MEDIA.odessa18l.src} srcSet={MEDIA.odessa18l.srcSet} sizes="104px" fetchPriority="high" width="240" height="438" alt="Odessa 18 L reusable water bottle" className="w-full" />
        <div className="absolute left-1/2 -top-3 flex h-12 w-12 -translate-x-1/2 flex-col items-center justify-center rounded-full bg-red-600 text-center text-white shadow-lg ring-2 ring-red-400/40">
          <span className="text-[8px] font-bold leading-none">18 L</span>
          <span className="font-display text-base font-black leading-none">$10</span>
        </div>
      </div>

      {/* Authentic dark-label Odessa bottles + price badges (major hero element, per comp) */}
      <div className="pointer-events-none absolute bottom-5 left-5 z-[9] hidden w-[30vw] max-w-[420px] md:block" data-testid="hero-bottles">
        <img src={MEDIA.odessaBottles.src} loading="lazy" alt="Odessa 18 L and 11 L reusable water bottles" className="w-full drop-shadow-[0_24px_50px_rgba(0,0,0,0.55)]" />
        <div className="absolute left-[4%] top-[10%] flex h-[4.5rem] w-[4.5rem] flex-col items-center justify-center rounded-full bg-red-600 text-center text-white shadow-lg ring-2 ring-red-400/40 md:h-24 md:w-24">
          <span className="text-[11px] font-bold leading-none md:text-sm">18 L</span>
          <span className="font-display text-2xl font-black leading-none md:text-3xl">$10</span>
        </div>
        <div className="absolute right-[10%] top-[26%] flex h-16 w-16 flex-col items-center justify-center rounded-full bg-red-600 text-center text-white shadow-lg ring-2 ring-red-400/40 md:h-20 md:w-20">
          <span className="text-[10px] font-bold leading-none md:text-xs">11 L</span>
          <span className="font-display text-xl font-black leading-none md:text-2xl">$7</span>
        </div>
      </div>

      {/* Authentic wrapped Capital Flow van integrated lower-right (per comp) */}
      <div className="pointer-events-none absolute bottom-0 right-0 z-[7] hidden w-[42vw] max-w-[620px] lg:block" data-testid="hero-van">
        <img src={MEDIA.van2.src} srcSet={MEDIA.van2.srcSet} sizes="42vw" loading="lazy" alt="Capital Flow wrapped water-delivery van in Ottawa" className="w-full"
          style={{
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 22%), linear-gradient(to top, transparent 0%, #000 28%)",
            WebkitMaskComposite: "source-in",
            maskImage: "linear-gradient(to right, transparent 0%, #000 22%), linear-gradient(to top, transparent 0%, #000 28%)",
            maskComposite: "intersect",
          }}
        />
      </div>

      {/* PROMO: the old hero badge was replaced by the SEO PromoBanner (Founding Customer Pricing, code FOUNDER)
          (top of homepage) + /ottawa-water-delivery-promotion page. To retire the promotion,
          restore the badge here and revert the PromoBanner edits. See src/lib/promoConfig.js. */}

      {/* Kinetic type */}
      <motion.div
        style={{ y: yText }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1600px] flex-col justify-end px-6 pb-16 md:px-10 md:pb-24 pointer-events-none"
      >
        <div
          className="anim-fadeup mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-aqua"
          style={{ animationDelay: "0.2s" }}
        >
          <span className="h-px w-10 bg-aqua" />
          {t.hero.eyebrow}
        </div>

        <h1 className="sr-only">Distilled Water Delivery in Ottawa &amp; the National Capital Region</h1>
        <div aria-hidden="true" className="font-display font-black uppercase leading-[0.82] tracking-tighter text-[16vw] sm:text-[13vw] md:text-[11vw] lg:text-[10.5vw]">
          <MaskLine delay={0.35}>{t.hero.title1}</MaskLine>
          <MaskLine delay={0.47}>
            {t.hero.title2a} <span className="text-aqua [-webkit-text-stroke:0] [paint-order:stroke] drop-shadow-[0_2px_12px_rgba(12,36,23,0.6)]">{t.hero.title2b}</span>
          </MaskLine>
        </div>

        <div className="mt-8 flex flex-col gap-7 lg:pl-[300px] lg:max-w-[820px]">
          <div className="anim-fadeup max-w-md" style={{ animationDelay: "1s" }}>
            <p className="text-base leading-relaxed text-white/70 md:text-lg">{t.hero.desc}</p>
          </div>

          <div
            className="anim-fadeup pointer-events-auto flex flex-wrap items-center gap-4"
            style={{ animationDelay: "1.2s" }}
          >
            <button
              onClick={() => scrollTo("#order")}
              className="inline-flex items-center gap-2 rounded-full bg-aqua px-8 py-4 text-sm font-bold text-abyss transition-transform duration-300 hover:scale-105 active:scale-95 glow-aqua"
              data-testid="hero-order-button"
            >
              {t.hero.order}
            </button>
            <Link
              to="/signup?business=1"
              className="inline-flex items-center gap-2 rounded-full border border-aqua/40 px-8 py-4 text-sm font-bold text-aqua transition-colors duration-300 hover:bg-aqua/10"
              data-testid="hero-business-button"
            >
              <Building2 className="h-4 w-4" />
              {t.hero.orderBusiness}
            </Link>
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:border-aqua hover:text-aqua"
              data-testid="hero-call-button"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phone}
            </a>
            <Link to="/login" className="pointer-events-auto w-full text-sm text-white/60 underline-offset-4 transition-colors hover:text-aqua hover:underline md:basis-full" data-testid="hero-signin-link">
              {t.hero.signinLine}
            </Link>
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/40 md:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        {lite ? (
          <ArrowDown className="h-4 w-4" />
        ) : (
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown className="h-4 w-4" />
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
